import { useCallback, useMemo } from 'react';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { findSegmentByDate } from '@/features/broadcast-schedule-program/helpers/find-segment-by-date';
import { TSegment } from '@/features/segments/types';

import { useGetMultipleSegmentThumbnails } from '../api/get-multiple-segment-thumbnails';
import { useGetSegmentThumbnails } from '../api/get-segment-thumbnails';
import { TSegmentThumbnail } from '../types';

type ThumbnailContext = {
  thumbnails: TSegmentThumbnail[];
  referenceStartDatetime: string | null;
};

type Resolvable = {
  segment_id?: string | null;
  start_datetime?: string | null;
  parameters?: {
    segment_id?: string | null;
  } | null;
};

function getSegmentId(item: Resolvable): string | null {
  return item.segment_id ?? item.parameters?.segment_id ?? null;
}

function useThumbnailContextResolver<T extends Resolvable>(items: T[]) {
  const { activeSegment, activeClip, globalViewSegments, segmentItems } =
    useBroadcastScheduleProgram();

  // Normal case: a single segment (or clip) as the spritesheet source.
  const fallbackReference = activeSegment?.start_datetime ?? activeClip?.start_datetime ?? null;
  const { data: fallbackThumbnails = [] } = useGetSegmentThumbnails({
    segmentId: activeSegment?.id,
  });

  const availableSegments = useMemo<TSegment[]>(() => {
    const byId = new Map<string, TSegment>();
    segmentItems.forEach(segment => byId.set(segment.id, segment));
    globalViewSegments.forEach(segment => byId.set(segment.id, segment));
    if (activeSegment) byId.set(activeSegment.id, activeSegment);
    return Array.from(byId.values());
  }, [activeSegment, globalViewSegments, segmentItems]);

  const segmentIdByItem = useMemo(() => {
    const map = new Map<T, string>();
    items.forEach(item => {
      const segmentId = getSegmentId(item);
      if (segmentId) {
        map.set(item, segmentId);
        return;
      }

      if (!item.start_datetime) return;
      const segment = findSegmentByDate(availableSegments, item.start_datetime);
      if (segment) map.set(item, segment.id);
    });
    return map;
  }, [availableSegments, items]);

  // Clips can span multiple segments, so collect every segment represented by
  // the presences and resolve thumbnails per presence instead of using the
  // currently active segment for the whole table.
  const segmentIds = useMemo(() => {
    const ids = new Set<string>();
    items.forEach(item => {
      const segmentId = segmentIdByItem.get(item);
      if (segmentId) ids.add(segmentId);
    });
    return Array.from(ids);
  }, [items, segmentIdByItem]);

  const thumbnailsBySegment = useGetMultipleSegmentThumbnails(segmentIds);

  const contextBySegment = useMemo<Record<string, ThumbnailContext>>(() => {
    const map: Record<string, ThumbnailContext> = {};
    availableSegments.forEach(seg => {
      const thumbs = thumbnailsBySegment[seg.id];
      if (thumbs) map[seg.id] = { thumbnails: thumbs, referenceStartDatetime: seg.start_datetime };
    });
    return map;
  }, [availableSegments, thumbnailsBySegment]);

  return useCallback(
    (item: T): ThumbnailContext => {
      const segmentId = segmentIdByItem.get(item);
      if (segmentId) {
        return (
          contextBySegment[segmentId] ?? {
            thumbnails: [],
            referenceStartDatetime: null,
          }
        );
      }
      return { thumbnails: fallbackThumbnails, referenceStartDatetime: fallbackReference };
    },
    [contextBySegment, fallbackThumbnails, fallbackReference, segmentIdByItem]
  );
}

export { useThumbnailContextResolver };
export type { ThumbnailContext };
